import {
    useState,
    useRef,
    type FC
} from "react";
import {
    View
} from "react-native";
import type IDataTableFilterSheetProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../types";
import {
    useDataTableContext,
    DataTableContext
} from "../../context";
import type {
    IBottomSheetRef
} from "../../../bottomSheet/type";
import type {
    DataTableSortDirection
} from "../../type";
import {
    ListFilter as ListFilterIcon
} from "lucide-react-native";
import {
    formatNumber
} from "../../../../helpers/locale";
import {
    DATA_TABLE_LISTBOX_PROPS
} from "../../util";
import ActionSheet from "../../../bottomSheet/components/actionSheet";
import DataTableFacetOptions from "../../internal/facetOptions";
import PopoverSurface from "../../../popoverSurface";
import OptionRow from "../../../optionRow";
import Button from "../../../button";

const SORT_DIRECTIONS: Array<DataTableSortDirection> = [
    "asc",
    "desc"
];

const DataTableFilterSheet: FC<IDataTableFilterSheetProps> = ({
    customTheme: customThemeProp,
    style,
    title
}) => {
    const context = useDataTableContext({
        part: "FilterSheet"
    });

    const {
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customThemeProp ?? context?.customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(context?.customLocalize);

    const [
        isOpen,
        setIsOpen
    ] = useState(false);

    const sheetRef = useRef<IBottomSheetRef>(null);

    const {
        group: groupDynamicStyle
    } = useStyles({
        spaces
    });

    if(!context || context.layout !== "cards") {
        return null;
    }

    const currentTheme = customThemeProp ?? context.customTheme;

    const facetColumns = context.allColumns.filter((column) => column.filterOptions);

    const sortColumns = context.sortBehaviour === "none" ? [] : context.allColumns.filter((column) => column.isSortable && !column.isHeaderHidden);

    if(facetColumns.length === 0 && sortColumns.length === 0) {
        return null;
    }

    const activeCount = Object.values(context.columnFilters).reduce((count, values) => count + values.length, 0);

    const countText = formatNumber({
        locale: activeLocale,
        value: activeCount
    });

    const renderIcon: NCoreUIKitIcon = ({
        customColor,
        color,
        size
    }) => <ListFilterIcon
        color={customColor ?? colors.content.icon[color]}
        size={size}
    />;

    const renderSortGroup = () => {
        if(sortColumns.length === 0) {
            return null;
        }

        const [
            primaryRule
        ] = context.sorting;

        return <View
            style={[
                stylesheet.group,
                groupDynamicStyle
            ]}
        >
            <PopoverSurface.Heading
                customTheme={currentTheme}
                variant="sheet"
            >
                {localize("data-table-sort")}
            </PopoverSurface.Heading>
            <View
                aria-label={localize("data-table-sort")}
                {...DATA_TABLE_LISTBOX_PROPS}
            >
                {
                    sortColumns.flatMap((column) => SORT_DIRECTIONS.map((direction) => <OptionRow
                        label={localize(direction === "asc" ? "data-table-sort-option-ascending" : "data-table-sort-option-descending", [
                            column.header
                        ])}
                        isSelected={primaryRule?.id === column.id && primaryRule.direction === direction}
                        onPress={() => context.handleSetSorting([
                            {
                                id: column.id,
                                direction
                            }
                        ])}
                        key={`${column.id}-${direction}`}
                        customTheme={currentTheme}
                        variant="sheet"
                        role="option"
                        tabIndex={0}
                    />))
                }
            </View>
        </View>;
    };

    return <>
        <Button
            aria-label={activeCount > 0 ? localize("data-table-filters-active", [
                countText
            ]) : undefined}
            title={activeCount > 0 ? localize("data-table-filters-count", [
                countText
            ]) : localize("data-table-filters")}
            isDisabled={context.isPermissionDenied}
            onPress={() => setIsOpen(true)}
            customTheme={currentTheme}
            aria-haspopup="dialog"
            icon={renderIcon}
            variant="outline"
            type="neutral"
            size="medium"
            style={style}
        />
        <ActionSheet
            title={title ?? localize("data-table-filter-and-sort")}
            description={localize("data-table-results-match", [
                formatNumber({
                    value: context.totalCount,
                    locale: activeLocale
                })
            ])}
            primaryAction={{
                title: localize("data-table-show-results", [
                    formatNumber({
                        value: context.totalCount,
                        locale: activeLocale
                    })
                ]),
                onPress: () => sheetRef.current?.close()
            }}
            action={activeCount > 0 ? {
                onPress: () => context.handleClearFilters(),
                title: localize("clear")
            } : undefined}
            customLocalize={context.customLocalize}
            onClosed={() => setIsOpen(false)}
            customTheme={currentTheme}
            isShowCancel={false}
            isActive={isOpen}
            ref={sheetRef}
        >
            <DataTableContext.Provider
                value={context}
            >
                {
                    facetColumns.map((column) => <View
                        style={[
                            stylesheet.group,
                            groupDynamicStyle
                        ]}
                        key={column.id}
                    >
                        <PopoverSurface.Heading
                            customTheme={currentTheme}
                            variant="sheet"
                        >
                            {column.header}
                        </PopoverSurface.Heading>
                        <DataTableFacetOptions
                            customTheme={currentTheme}
                            columnId={column.id}
                            variant="sheet"
                        />
                    </View>)
                }
                {renderSortGroup()}
            </DataTableContext.Provider>
        </ActionSheet>
    </>;
};
export default DataTableFilterSheet;
